import { TileGridProps } from "./sharedTypes.js";

/**
 * Packs each resource‐group's dates into as few rows as possible,
 * giving longer spans priority in the upper tracks.
 *
 * @param {TileGridProps["teamSchedule"]} resources   – your array of { resourceId, rowLabel, dates: [...] }
 * @param {Date}            viewStart – midnight at the start of your calendar view
 * @returns {Array<Object>}           – a new array of resources, each date now has an updated `.row`
 */
export function packTileView(
  schedule: NonNullable<TileGridProps["teamSchedule"]>,
  viewStart: Date
) {
  const MS_PER_DAY = 24 * 60 * 60 * 1000;

  // for each resource group…
  for (const group of schedule) {
    // build a tiny helper array of events with day‐indices
    const events = group.dates.map((d) => {
      const s = new Date(d.start_time);
      const e = new Date(d.end_time);
      const startIdx = Math.floor(
        (s.getTime() - viewStart.getTime()) / MS_PER_DAY
      );
      const endIdx = Math.floor(
        (e.getTime() - viewStart.getTime()) / MS_PER_DAY
      );
      return { original: d, startIdx, endIdx };
    });

    // sort by startIdx asc; if two start together, longer first
    events.sort((a, b) =>
      a.startIdx !== b.startIdx ? a.startIdx - b.startIdx : b.endIdx - a.endIdx
    );

    // greedy row‐allocator
    const tracks: number[] = []; // tracks[row] = endIdx of last event in that row

    for (const ev of events) {
      let placed = false;
      for (let r = 0; r < tracks.length; r++) {
        if (tracks[r] < ev.startIdx) {
          // reuse free row
          ev.original.row = r;
          tracks[r] = ev.endIdx;
          placed = true;
          break;
        }
      }
      if (!placed) {
        // no free row, open a new one
        const newRow = tracks.length;
        ev.original.row = newRow;
        tracks.push(ev.endIdx);
      }
    }
  }

  return schedule;
}

// ————————————————
// Example usage with your data:
